import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  LabelList,
  BarChart as RechartsBarChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  type ChartDataPoint,
  type ChartSeries,
  DEFAULT_CHART_COLORS,
  sanitizeKey,
  ChartLegend as WrapperChartLegend,
} from '@/components/molecules/charts/LineChart';
import {
  CHART_NEUTRAL_COLOR,
  CHART_PALETTE,
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  useChartKeyColors,
} from '@/components/ui/chart';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { useSeriesHighlight } from '@/hooks/use-series-highlight';
import { cn } from '@/lib/utils';
import {
  estimateChartAxisWidth,
  getFiniteChartValue,
  getNonNegativeChartValue,
  toSeriesChartData,
} from '../chart-data';

// Stacked bars: neutral baseline first, then secondary + primary on top.
// Grouped bars fall through to DEFAULT_CHART_COLORS (full categorical palette).
const DEFAULT_BAR_COLORS = [
  CHART_NEUTRAL_COLOR, // Series 1 — baseline / "other"
  CHART_PALETTE[1], // Series 2 — secondary
  CHART_PALETTE[0], // Series 3 — primary highlight
];

const barChartVariants = cva('relative w-full', {
  variants: {
    size: {
      sm: 'h-40',
      default: 'h-60',
      lg: 'h-80',
      xl: 'h-96',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export interface BarChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof barChartVariants> {
  /** Data points for the chart */
  data: ChartDataPoint[];
  /** Series configuration */
  series: ChartSeries[];
  /** Show Y-axis labels and grid lines */
  showYAxis?: boolean;
  /** Show X-axis labels */
  showXAxis?: boolean;
  /** Show grid lines */
  showGrid?: boolean;
  /** Y-axis label (title) */
  yAxisLabel?: string;
  /** X-axis label (title) */
  xAxisLabel?: string;
  /** Legend position */
  legendPosition?: BarLegendPosition;
  /** Custom Y-axis tick values */
  yAxisTicks?: number[];
  /** Format function for Y-axis values */
  yAxisFormat?: (value: number) => string;
  /** Bar display mode */
  barMode?: 'stacked' | 'grouped';
  /** Bar direction */
  orientation?: 'vertical' | 'horizontal';
  /** Color each category from the HUMAIN chart palette */
  colorByCategory?: boolean;
  /** Show value labels on bars */
  showValueLabels?: boolean;
  /** Value-label placement */
  valueLabelPosition?: 'top' | 'right' | 'insideLeft';
  /** Border radius for bars */
  barRadius?: number;
  /** Gap between bar groups (0-1) */
  barGap?: number;
  /** Pixel gap between bars in a group - only for grouped mode */
  barGroupGap?: number;
  /** Show tooltip on hover */
  showTooltip?: boolean;
  /** Accessible label for screen readers */
  'aria-label'?: string;
}

export type BarLegendPosition = 'none' | 'top' | 'bottom' | 'right';

function BarChart({
  data,
  series,
  showYAxis = true,
  showXAxis = true,
  showGrid = true,
  yAxisLabel,
  xAxisLabel,
  legendPosition = 'none',
  yAxisTicks,
  yAxisFormat = (v) => v.toLocaleString(),
  barMode = 'stacked',
  orientation = 'vertical',
  colorByCategory = false,
  showValueLabels = false,
  valueLabelPosition = 'top',
  barRadius = 4,
  barGap = 0.2,
  barGroupGap = 4,
  showTooltip = true,
  size,
  className,
  'aria-label': ariaLabel,
  ...props
}: BarChartProps) {
  const prefersReducedMotion = useReducedMotion();
  const { highlightedSeries, setHighlightedSeries, getOpacity } =
    useSeriesHighlight(prefersReducedMotion);

  const chartData = React.useMemo(
    () => toSeriesChartData(data, series),
    [data, series],
  );
  // A mode/order change has no honest positional correspondence. Remount only
  // marks, not axes/legend, so unrelated rows never inherit obsolete geometry.
  const topology = JSON.stringify([
    orientation,
    barMode,
    data.map((point) => [point.id, point.label]),
    series.map((item) => item.id),
    // Reset Recharts' private interruption snapshot at preference boundaries.
    prefersReducedMotion,
  ]);
  // Arm only data updates, never mount, topology replacement or a preference
  // change: reenabling Recharts on the same input would replay its old snapshot.
  const [motionInput, setMotionInput] = React.useState({
    data,
    topology,
    animate: false,
  });
  if (motionInput.data !== data || motionInput.topology !== topology) {
    setMotionInput({
      data,
      topology,
      animate: motionInput.topology === topology && !prefersReducedMotion,
    });
  }
  const finiteBarRadius = getNonNegativeChartValue(barRadius, 4);
  const finiteBarGap = getNonNegativeChartValue(barGap, 0.2);
  const finiteBarGroupGap = getFiniteChartValue(barGroupGap, 4);
  const yAxisWidth = React.useMemo(
    () =>
      estimateChartAxisWidth({
        data,
        series,
        ticks: yAxisTicks,
        formatter: yAxisFormat,
        includeSeriesTotals: barMode === 'stacked',
        minWidth: 48,
        maxWidth: 112,
      }),
    [barMode, data, series, yAxisFormat, yAxisTicks],
  );
  const categoryAxisWidth = React.useMemo(
    () =>
      estimateChartAxisWidth({
        labels: data.map((item) => item.label),
        minWidth: 60,
        maxWidth: 192,
      }),
    [data],
  );

  const keyColors = useChartKeyColors(
    series.map((item, index) => item.id ?? index),
    barMode === 'stacked' ? DEFAULT_BAR_COLORS : DEFAULT_CHART_COLORS,
  );
  const categoryColors = useChartKeyColors(
    data.map((item, index) => item.id ?? index),
  );
  const legendSeries = series.map((item, index) => ({
    ...item,
    color: item.color || keyColors.get(item.id ?? index),
  }));
  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {};
    for (const [i, s] of series.entries()) {
      const key = sanitizeKey(s.id);
      config[key] = {
        label: s.label,
        color: s.color || keyColors.get(s.id ?? i),
      };
    }
    return config;
  }, [series, keyColors]);
  const renderSeries = React.useMemo(
    () => (barMode === 'stacked' ? [...series].reverse() : series),
    [barMode, series],
  );

  return (
    <div
      data-slot="bar-chart"
      className={cn(barChartVariants({ size }), className)}
      {...props}
    >
      <div className="flex h-full gap-1">
        {/* Y-axis label */}
        {yAxisLabel && (
          <div className="flex items-center justify-center pb-6">
            <span
              className="text-xs font-medium text-muted-foreground whitespace-nowrap"
              style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
              }}
            >
              {yAxisLabel}
            </span>
          </div>
        )}

        {/* Main chart area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top legend */}
          {legendPosition === 'top' && (
            <div className="flex justify-end pb-2">
              <WrapperChartLegend
                series={legendSeries}
                position="top"
                onSeriesHover={setHighlightedSeries}
                highlightedSeries={highlightedSeries}
              />
            </div>
          )}

          {/* Chart with axes */}
          <div className="flex-1 flex min-h-0 min-w-0">
            <ChartContainer
              role="img"
              aria-label={ariaLabel || 'Bar chart'}
              aria-labelledby={props['aria-labelledby']}
              config={chartConfig}
              dir="ltr"
              className="flex-1 min-w-0 min-h-0 aspect-auto"
            >
              <RechartsBarChart
                data={chartData}
                stackOffset="sign"
                layout={
                  orientation === 'horizontal' ? 'vertical' : 'horizontal'
                }
                barCategoryGap={`${finiteBarGap * 100}%`}
                barGap={barMode === 'grouped' ? finiteBarGroupGap : 0}
                margin={{
                  top: 8,
                  right: 8,
                  bottom: xAxisLabel ? 20 : 0,
                  left: 0,
                }}
              >
                {showGrid && (
                  <CartesianGrid
                    strokeDasharray="0"
                    vertical={orientation === 'horizontal'}
                    horizontal={orientation !== 'horizontal'}
                    className="stroke-border"
                  />
                )}
                {orientation === 'vertical' && (
                  <XAxis
                    hide={!showXAxis}
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                  />
                )}
                {orientation === 'vertical' && (
                  <YAxis
                    hide={!showYAxis}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                    tickFormatter={yAxisFormat}
                    ticks={yAxisTicks}
                    width={yAxisWidth}
                  />
                )}
                {orientation === 'horizontal' && (
                  <XAxis
                    hide={!showXAxis}
                    type="number"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                    tickFormatter={yAxisFormat}
                    ticks={yAxisTicks}
                  />
                )}
                {orientation === 'horizontal' && (
                  <YAxis
                    hide={!showYAxis}
                    type="category"
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                    width={categoryAxisWidth}
                  />
                )}
                {showTooltip && (
                  <ChartTooltip
                    content={<ChartTooltipContent />}
                    cursor={
                      prefersReducedMotion ? false : { fill: 'var(--muted)' }
                    }
                  />
                )}
                {legendPosition === 'bottom' && (
                  <ChartLegend content={<ChartLegendContent />} />
                )}
                {/* Bars - render in reverse order for stacked so first series is at bottom */}
                {renderSeries.map((s, i) => {
                  const key = sanitizeKey(s.id);
                  const isOuterBar =
                    barMode === 'stacked' && i === series.length - 1;
                  return (
                    <Bar
                      key={`${topology}-${key}`}
                      dataKey={key}
                      stackId={barMode === 'stacked' ? 'stack' : undefined}
                      fill={`var(--color-${key})`}
                      radius={
                        barMode === 'stacked'
                          ? isOuterBar
                            ? orientation === 'horizontal'
                              ? [0, finiteBarRadius, finiteBarRadius, 0]
                              : [finiteBarRadius, finiteBarRadius, 0, 0]
                            : [0, 0, 0, 0]
                          : orientation === 'horizontal'
                            ? [0, finiteBarRadius, finiteBarRadius, 0]
                            : [
                                finiteBarRadius,
                                finiteBarRadius,
                                finiteBarRadius,
                                finiteBarRadius,
                              ]
                      }
                      className={cn(
                        '[&.recharts-bar]:transition-opacity [&.recharts-bar]:duration-(--duration-moderate) [&.recharts-bar]:ease-standard motion-reduce:[&.recharts-bar]:transition-none',
                        getOpacity(s.id) === 0.2
                          ? '[&.recharts-bar]:opacity-20'
                          : '[&.recharts-bar]:opacity-100',
                      )}
                      isAnimationActive={
                        motionInput.animate && !prefersReducedMotion
                      }
                      animationDuration={200}
                      animationEasing="ease-out"
                    >
                      {colorByCategory &&
                        data.map((entry, entryIndex) => (
                          <Cell
                            key={`${key}-${String(entry.id ?? entryIndex)}`}
                            fill={
                              s.color ||
                              categoryColors.get(entry.id ?? entryIndex)
                            }
                          />
                        ))}
                      {showValueLabels && (
                        <LabelList
                          dataKey={key}
                          position={valueLabelPosition}
                          className="fill-foreground text-[10px]"
                          formatter={(value) => yAxisFormat(Number(value))}
                        />
                      )}
                    </Bar>
                  );
                })}
              </RechartsBarChart>
            </ChartContainer>

            {/* Right legend */}
            {legendPosition === 'right' && (
              <div className="ps-3 flex items-start pt-2">
                <WrapperChartLegend
                  series={legendSeries}
                  position="right"
                  onSeriesHover={setHighlightedSeries}
                  highlightedSeries={highlightedSeries}
                />
              </div>
            )}
          </div>

          {/* X-axis label */}
          {xAxisLabel && (
            <div className="flex justify-center pt-2">
              <span className="text-xs font-medium text-muted-foreground">
                {xAxisLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

BarChart.displayName = 'BarChart';

export { BarChart, barChartVariants };
